import React, { useLayoutEffect, useState, useEffect } from "react";

const CSS_PIXELS_PER_MM = 96 / 25.4;
const FIT_PADDING = 16;
const MIN_STAGE_HEIGHT = 280;
const DETAIL_GAP = 12;
const MIN_DETAIL_MAGNIFICATION = 2;
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const dimensions = (value) => value?.width && value?.height ? `${value.width.toLocaleString()} × ${value.height.toLocaleString()} px` : "—";

function useElementSize() {
  const [element, setElement] = useState(null);
  const [size, setSize] = useState(null);
  useLayoutEffect(() => {
    if (!element) return setSize(null);
    const measure = () => setSize((current) => {
      const next = { width: element.clientWidth, height: element.clientHeight };
      return current?.width === next.width && current?.height === next.height ? current : next;
    });
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);
  return [setElement, size];
}

function Spinner({ label }) {
  return <div role="status" aria-live="polite" className="absolute inset-0 flex flex-col items-center justify-center gap-3 pointer-events-none">
    <div className="animate-spin rounded-full border-2 w-7 h-7 border-white/20 border-t-white/80" />
    <span className="text-xs text-white/70">{label}</span>
  </div>;
}

function Chip({ title, detail, className = "" }) {
  return <div className={`absolute rounded bg-[#15171a]/85 px-2.5 py-1.5 text-xs leading-tight text-white/80 pointer-events-none ${className}`}>
    <div>{title}</div>
    {detail && <div className="font-mono text-white">{detail}</div>}
  </div>;
}

export const megapixels = (value) => value?.width && value?.height ? `${(value.width * value.height / 1_000_000).toFixed(1)} MP` : "—";
const withDpi = (value, dpi, format = dimensions) => dpi ? `${format(value)} · ${dpi} DPI` : format(value);

export default function UpscaleInspector({ operation, onReady, onDetailChange, showHint = true, showMegapixels = false }) {
  const format = showMegapixels ? megapixels : dimensions;
  const width = operation.after_dimensions?.width || 1000;
  const height = operation.after_dimensions?.height || 1000;
  const printWidth = operation.print_dimensions_mm?.width;
  const printHeight = operation.print_dimensions_mm?.height;
  const hasPrintSize = [printWidth, printHeight].every((value) => Number.isFinite(value) && value > 0);
  const [point, setPoint] = useState({ x: 0.5, y: 0.5 });
  const [loaded, setLoaded] = useState({});
  const [failed, setFailed] = useState(false);
  const [areaRef, area] = useElementSize();
  const [panelRef, panel] = useElementSize();
  useEffect(() => {
    setLoaded({});
    setFailed(false);
  }, [operation.before_url, operation.after_url, operation.before_preview_url, operation.after_preview_url]);

  const detailSize = hasPrintSize ? { width: Math.round(printWidth * CSS_PIXELS_PER_MM), height: Math.round(printHeight * CSS_PIXELS_PER_MM) } : { width, height };
  const detailScale = hasPrintSize ? "Est. print size" : "100%";
  const detailHeight = clamp(Math.round(window.innerHeight * 0.26), 160, 340);
  const fitScaleFor = (stageHeight) => area?.width > 0 && stageHeight > 0 ?
    Math.max(Math.min((area.width - FIT_PADDING * 2) / width, (stageHeight - FIT_PADDING * 2) / height), 0) : null;
  // Judge magnification against the Fit view as it would look with the panels
  // shown, so hiding them (which enlarges the Fit view) can't flip the decision.
  const stageWithDetail = Math.max((area?.height || 0) - detailHeight - DETAIL_GAP, MIN_STAGE_HEIGHT);
  const scaleWithDetail = fitScaleFor(stageWithDetail);
  const showDetail = Boolean(scaleWithDetail) && detailSize.width / (width * scaleWithDetail) >= MIN_DETAIL_MAGNIFICATION;
  useEffect(() => { onDetailChange?.(showDetail); }, [showDetail, onDetailChange]);
  const fitScale = showDetail ? scaleWithDetail : fitScaleFor(Math.max(area?.height || 0, MIN_STAGE_HEIGHT));
  const frameSize = fitScale ? { width: width * fitScale, height: height * fitScale } : { width: "100%", aspectRatio: `${width}/${height}` };
  const markLoaded = (key) => () => setLoaded((current) => ({ ...current, [key]: true }));
  const onError = () => setFailed(true);
  const comparisonReady = loaded.before && loaded.after;
  useEffect(() => { onReady?.(Boolean(comparisonReady) && !failed); }, [comparisonReady, failed, onReady]);

  const track = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    setPoint({ x: clamp((event.clientX - rect.left) / rect.width, 0, 1), y: clamp((event.clientY - rect.top) / rect.height, 0, 1) });
  };
  const nudge = (event) => {
    const step = event.shiftKey ? 0.1 : 0.02;
    const moves = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
    const move = moves[event.key];
    if (!move) return;
    event.preventDefault();
    setPoint((current) => ({ x: clamp(current.x + move[0], 0, 1), y: clamp(current.y + move[1], 0, 1) }));
  };

  // Both detail images render at the same size so they always show the same area.
  const offset = (panelSize, imageSize, focus) => imageSize <= panelSize ? (panelSize - imageSize) / 2 :
    clamp(panelSize / 2 - focus * imageSize, panelSize - imageSize, 0);
  const detailTransform = panel ? `translate3d(${offset(panel.width, detailSize.width, point.x)}px, ${offset(panel.height, detailSize.height, point.y)}px, 0)` : undefined;
  const detailImage = (key, src, alt) => <img src={src} alt={alt} draggable={false} fetchpriority="low" decoding="async" onLoad={markLoaded(key)} onError={onError}
    style={{ position: "absolute", left: 0, top: 0, ...detailSize, maxWidth: "none", transform: detailTransform, transformOrigin: "0 0", willChange: "transform" }} />;

  return <div className="flex flex-col flex-1 min-h-0 gap-3">
    {showHint && <p className="text-xs text-[#6B6B66]">
      Move over the image to compare.{showDetail && (hasPrintSize ?
        ` The detail view below shows that area at its approximate printed size (${Number(printWidth.toFixed(1))} × ${Number(printHeight.toFixed(1))} mm).` :
        " The detail view below shows that area at 100%.")}
    </p>}
    {failed && <p role="alert" className="text-sm text-rose-800">A comparison image could not load. Please reload this page.</p>}

    <div ref={areaRef} className="flex flex-col flex-1 min-h-0" style={{ gap: DETAIL_GAP }}>
      <div className="relative flex-1 rounded-md bg-[#15171a] overflow-hidden" style={{ minHeight: MIN_STAGE_HEIGHT }}>
        <div role="region" aria-label="Image comparison" className="absolute inset-0 flex overflow-auto">
          <div role="slider" tabIndex={0} aria-label="Comparison divider" aria-valuemin={0} aria-valuemax={100}
            aria-valuenow={Math.round(point.x * 100)} aria-valuetext={`${Math.round(point.x * 100)}% original`}
            onPointerMove={track} onPointerDown={track} onKeyDown={nudge}
            className="relative m-auto shrink-0 select-none outline-none focus-visible:ring-2 focus-visible:ring-white/70"
            style={{ ...frameSize, cursor: "none", touchAction: "pan-y" }}>
            <img src={operation.after_preview_url || operation.after_url} alt={`${operation.model || "Upscaled"} result`} draggable={false}
              onLoad={markLoaded("after")} onError={onError} className="absolute inset-0 block w-full h-full" style={{ objectFit: "fill", maxWidth: "none" }} />
            <img src={operation.before_preview_url || operation.before_url} alt="Original image" draggable={false}
              onLoad={markLoaded("before")} onError={onError} className="absolute inset-0 block w-full h-full"
              style={{ objectFit: "fill", maxWidth: "none", clipPath: `inset(0 ${100 - point.x * 100}% 0 0)` }} />
            {comparisonReady && <>
              <div aria-hidden="true" className="absolute top-0 bottom-0 pointer-events-none bg-white/70"
                style={{ left: `${point.x * 100}%`, width: 1, transform: "translateX(-0.5px)" }} />
              <div aria-hidden="true" className="absolute rounded-full pointer-events-none"
                style={{ left: `${point.x * 100}%`, top: `${point.y * 100}%`, width: 28, height: 28, transform: "translate(-50%, -50%)",
                  border: "1.5px solid rgba(255,255,255,0.9)", boxShadow: "0 0 0 1px rgba(0,0,0,0.25)" }} />
            </>}
          </div>
        </div>
        {!comparisonReady && !failed && <Spinner label="Loading images…" />}
        <Chip title="Before:" detail={withDpi(operation.before_dimensions, operation.before_dpi, format)} className="left-3 bottom-3" />
        <Chip title="After:" detail={withDpi(operation.after_dimensions, operation.after_dpi, format)} className="right-3 bottom-3 text-right" />
      </div>

      {showDetail && <div aria-label={`Detail at ${detailScale}`} className="grid grid-cols-2 gap-1 shrink-0" style={{ height: detailHeight }}>
        <div ref={panelRef} className="relative overflow-hidden rounded-md bg-[#15171a]">
          {detailImage("detailBefore", operation.before_url, `Original detail at ${detailScale}`)}
          {!loaded.detailBefore && !failed && <Spinner label="Loading detail…" />}
          <Chip title="Before" className="left-2 top-2" />
        </div>
        <div className="relative overflow-hidden rounded-md bg-[#15171a]">
          {detailImage("detailAfter", operation.after_url, `Upscaled detail at ${detailScale}`)}
          {!loaded.detailAfter && !failed && <Spinner label="Loading detail…" />}
          <Chip title="After" className="right-2 top-2 text-right" />
        </div>
      </div>}
    </div>
  </div>;
}
